import React, { useMemo, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { createAppTheme } from './theme';
import { useThemeStore, themePalettes } from './stores/themeStore';
import { useAuthStore } from './stores/authStore';
import { useSettingsStore } from './stores/settingsStore';
import { useComposeTabsStore } from './stores/composeTabsStore';
import { BrandingProvider } from './utils/BrandingContext';
import { isHelpdeskMode, isCustomDomain } from './utils/helpdeskMode';
import { MailLayout } from './components/layout';
import HybridMailLayout from './components/layout/HybridMailLayout';
import { requestNotificationPermission } from './services/notificationService';
import { Login, ForgotPassword, SignInForm, ResetPassword } from './pages/auth';
import LandingPage from './pages/LandingPage';
import ContactPage from './pages/ContactPage';
import FeaturesPage from './pages/FeaturesPage';
import EnterprisePage from './pages/EnterprisePage';
import MigrationPage from './pages/MigrationPage';
import AboutPage from './pages/AboutPage';
import BlogPage from './pages/BlogPage';
import CareersPage from './pages/CareersPage';
import HelpCenterPage from './pages/HelpCenterPage';
import DocumentationPage from './pages/DocumentationPage';
import StatusPage from './pages/StatusPage';
import CommunityPage from './pages/CommunityPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import AcceptableUsePolicyPage from './pages/AcceptableUsePolicyPage';
import DataProcessingAgreementPage from './pages/DataProcessingAgreementPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import SLAPage from './pages/SLAPage';
import SecurityPage from './pages/SecurityPage';
import SmallBusinessPage from './pages/SmallBusinessPage';
import StartupsPage from './pages/StartupsPage';
import HealthcarePage from './pages/HealthcarePage';
import EducationPage from './pages/EducationPage';
import NonProfitPage from './pages/NonProfitPage';
import { EmailListView } from './components/mail';
import { AIAssistant } from './pages/ai';
import CalendarPage from './pages/calendar/CalendarPage';
import TasksPage from './pages/tasks/TasksPage';
import ContactsPage from './pages/contacts/ContactsPage';
import NotesPage from './pages/notes/NotesPage';
import { StreamsPage } from './pages/streams';
import { BookmarksPage } from './pages/bookmarks';
import SupportPage from './pages/support/SupportPage';
import DocumentEditor from './pages/docs/DocumentEditor';
import AttachmentEditor from './pages/docs/AttachmentEditor';
import DrivexPage from './pages/drivex/DrivexPage';
import HelpdeskLayout from './components/helpdesk/HelpdeskLayout';
import HelpdeskDashboard from './pages/helpdesk/HelpdeskDashboard';
import HelpdeskTickets from './pages/helpdesk/HelpdeskTickets';
import HelpdeskTicketDetail from './pages/helpdesk/HelpdeskTicketDetail';
import HelpdeskAgentQueue from './pages/helpdesk/HelpdeskAgentQueue';
import HelpdeskCustomers from './pages/helpdesk/HelpdeskCustomers';
import HelpdeskKnowledgeBase from './pages/helpdesk/HelpdeskKnowledgeBase';
import HelpdeskReports from './pages/helpdesk/HelpdeskReports';
import HelpdeskSettings from './pages/helpdesk/HelpdeskSettings';
import HelpdeskPortalLogin from './pages/helpdesk/HelpdeskPortalLogin';
import HelpdeskPortalTickets from './pages/helpdesk/HelpdeskPortalTickets';
import HelpdeskPortalTicketDetail from './pages/helpdesk/HelpdeskPortalTicketDetail';
import { usePortalAuthStore } from './stores/portalAuthStore';
import { AnalyticsPage } from './pages/analytics';
import TeamChatPage from './components/chat/TeamChatPage';
import ProfilePage from './pages/profile/ProfilePage';
import GuidePage from './pages/guide/GuidePage';
import HelpPage from './pages/help/HelpPage';
import {
  PlanSelection,
  OrganizationSignup,
  VerifyOTP,
  DomainSetup,
  DomainVerification,
  AdminUserSetup,
  DNSSetup,
  AddUsersSetup,
  SetupComplete,
} from './pages/setup';
import {
  SettingsLayout,
  SystemSettings,
  KeyboardShortcuts,
  IntegrationsSettings,
  NotificationSettings,
  ComposeSettings,
  SignatureSettings,
  OutOfOfficeSettings,
  FoldersSettings,
  LabelsSettings,
  AccountSettings,
  MailAccountsSettings,
  AntiSpamSettings,
  FiltersSettings,
  ImportExportSettings,
  MailViewOptions,
  AppPasswordsSettings,
} from './pages/settings';

// Protected Route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, token } = useAuthStore();
  const location = useLocation();
  
  // Also check localStorage directly — the interceptor may have refreshed the token
  // before Zustand's persisted state rehydrates
  const hasToken = token || localStorage.getItem('accessToken');
  
  if (!isAuthenticated && !hasToken) {
    // Native Capacitor app or deeper routes → direct sign-in form; root path on web → landing page
    const isNative = !!(window as any).Capacitor?.isNativePlatform?.();
    const target = (isNative || location.pathname !== '/') ? '/login/direct' : '/login';
    return <Navigate to={target} replace />;
  }
  
  return <>{children}</>;
};

// Portal Protected Route — requires portal user auth
const PortalProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = usePortalAuthStore();
  if (!isAuthenticated) return <Navigate to="/portal/login" replace />;
  return <>{children}</>;
};

// Helpdesk guard — redirects non-support routes to /support in helpdesk mode
const HelpdeskGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = window.location.pathname;
  if (isHelpdeskMode() && !location.startsWith('/support') && !location.startsWith('/login') && !location.startsWith('/forgot-password') && !location.startsWith('/reset-password') && !location.startsWith('/portal')) {
    return <Navigate to="/support" replace />;
  }
  return <>{children}</>;
};

// Redirect /compose to inbox and open compose as tab
const ComposeRedirect: React.FC = () => {
  const { getOrCreateTab } = useComposeTabsStore();
  useEffect(() => { getOrCreateTab(); }, []);
  return <Navigate to="/mail/inbox" replace />;
};

// Redirect /chat URL to open chat as tab inside mail layout
const ChatRedirect: React.FC = () => {
  const { openChat } = useComposeTabsStore();
  useEffect(() => { openChat(); }, []);
  return <Navigate to="/mail/inbox" replace />;
};

// Redirect /docs URL to open Documents as inner tab
const DocsRedirect: React.FC = () => {
  const { openDocs } = useComposeTabsStore();
  useEffect(() => { openDocs(); }, []);
  return <Navigate to="/mail/inbox" replace />;
};

// Redirect /sheets URL to open Spreadsheets as inner tab
const SheetsRedirect: React.FC = () => {
  const { openSheets } = useComposeTabsStore();
  useEffect(() => { openSheets(); }, []);
  return <Navigate to="/mail/inbox" replace />;
};

// Public Route wrapper (redirect if already logged in)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  
  if (isAuthenticated) {
    return <Navigate to={isHelpdeskMode() ? '/support/dashboard' : '/mail/inbox'} replace />;
  }
  
  return <>{children}</>;
};

const AppContent: React.FC = () => {
  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              {(window as any).Capacitor?.isNativePlatform?.()
                ? <Navigate to="/login/direct" replace />
                : isCustomDomain()
                  ? <Login />
                  : <LandingPage />}
            </PublicRoute>
          }
        />
        <Route
          path="/login/direct"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <PublicRoute>
              <PlanSelection />
            </PublicRoute>
          }
        />
        <Route
          path="/pricing"
          element={
            <PublicRoute>
              <PlanSelection />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <PlanSelection />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/signin-form"
          element={
            <PublicRoute>
              <SignInForm />
            </PublicRoute>
          }
        />
        <Route
          path="/contact"
          element={<ContactPage />}
        />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/enterprise" element={<EnterprisePage />} />
        <Route path="/migration" element={<MigrationPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/careers" element={<CareersPage />} />
        <Route path="/help-center" element={<HelpCenterPage />} />
        <Route path="/documentation" element={<DocumentationPage />} />
        <Route path="/status" element={<StatusPage />} />
        <Route path="/community" element={<CommunityPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/cookie-policy" element={<CookiePolicyPage />} />
        <Route path="/acceptable-use-policy" element={<AcceptableUsePolicyPage />} />
        <Route path="/data-processing-agreement" element={<DataProcessingAgreementPage />} />
        <Route path="/refund-policy" element={<RefundPolicyPage />} />
        <Route path="/sla" element={<SLAPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/small-business" element={<SmallBusinessPage />} />
        <Route path="/startups" element={<StartupsPage />} />
        <Route path="/healthcare" element={<HealthcarePage />} />
        <Route path="/education" element={<EducationPage />} />
        <Route path="/nonprofit" element={<NonProfitPage />} />
        <Route path="/mail/help" element={<HelpPage />} />

        {/* Setup wizard routes */}
        <Route path="/setup" element={<PlanSelection />} />
        <Route path="/setup/plans" element={<PlanSelection />} />
        <Route path="/setup/organization" element={<OrganizationSignup />} />
        <Route path="/setup/verify" element={<VerifyOTP />} />
        <Route path="/setup/domain" element={<DomainSetup />} />
        <Route path="/setup/verify-domain" element={<DomainVerification />} />
        <Route path="/setup/admin-user" element={<AdminUserSetup />} />
        <Route path="/setup/dns" element={<DNSSetup />} />
        <Route path="/setup/users" element={<AddUsersSetup />} />
        <Route path="/setup/complete" element={<SetupComplete />} />

        {/* Document editor - full screen, outside MailLayout */}
        <Route
          path="/editor/:id"
          element={
            <ProtectedRoute>
              <DocumentEditor />
            </ProtectedRoute>
          }
        />

        {/* Attachment editor - full screen OnlyOffice for email attachments */}
        <Route
          path="/attachment-editor"
          element={
            <ProtectedRoute>
              <AttachmentEditor />
            </ProtectedRoute>
          }
        />

        {/* Protected routes */}
        {/* Portal routes — standalone, no helpdesk staff layout required */}
        {isHelpdeskMode() && (
          <Route path="/portal/login" element={<HelpdeskPortalLogin />} />
        )}
        {isHelpdeskMode() && (
          <Route path="/portal/tickets" element={<PortalProtectedRoute><HelpdeskPortalTickets /></PortalProtectedRoute>} />
        )}
        {isHelpdeskMode() && (
          <Route path="/portal/tickets/:id" element={<PortalProtectedRoute><HelpdeskPortalTicketDetail /></PortalProtectedRoute>} />
        )}

        {/* Helpdesk mode: dedicated layout and routes */}
        {isHelpdeskMode() && (
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <HelpdeskLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/support/dashboard" replace />} />
            <Route path="support/dashboard" element={<HelpdeskDashboard />} />
            <Route path="support/tickets" element={<HelpdeskTickets />} />
            <Route path="support/tickets/:id" element={<HelpdeskTicketDetail />} />
            <Route path="support/queue" element={<HelpdeskAgentQueue />} />
            <Route path="support/customers" element={<HelpdeskCustomers />} />
            <Route path="support/knowledge" element={<HelpdeskKnowledgeBase />} />
            <Route path="support/reports" element={<HelpdeskReports />} />
            <Route path="support/settings" element={<HelpdeskSettings />} />
            <Route path="support" element={<Navigate to="/support/dashboard" replace />} />
            <Route path="support/:id" element={<HelpdeskTicketDetail />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="*" element={<Navigate to="/support/dashboard" replace />} />
          </Route>
        )}

        {/* Normal webmail mode */}
        {!isHelpdeskMode() && (
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <HybridMailLayout />
            </ProtectedRoute>
          }
        >
            <>
              {/* Mail routes */}
              <Route index element={<Navigate to="/mail/inbox" replace />} />
              <Route path="mail" element={<Navigate to="/mail/inbox" replace />} />
              <Route path="mail/guide" element={<GuidePage />} />
              <Route path="mail/:folder" element={<EmailListView />} />
              <Route path="mail/:folder/:emailId" element={<EmailListView />} />
              
              {/* Compose route - redirects to inbox and opens compose tab */}
              <Route path="compose" element={<ComposeRedirect />} />

              {/* AI Assistant */}
              <Route path="ai" element={<AIAssistant />} />
              <Route path="ai-assistant" element={<AIAssistant />} />

              {/* Other app routes */}
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="tasks" element={<TasksPage />} />
              <Route path="notes" element={<NotesPage />} />
              <Route path="contacts" element={<ContactsPage />} />
              <Route path="streams" element={<StreamsPage />} />
              <Route path="bookmarks" element={<BookmarksPage />} />
              <Route path="support" element={<SupportPage />} />
              <Route path="support/:ticketId" element={<SupportPage />} />
              <Route path="docs" element={<DocsRedirect />} />
              <Route path="sheets" element={<SheetsRedirect />} />
              <Route path="drivex" element={<DrivexPage />} />
              <Route path="drivex/folder/:folderId" element={<DrivexPage />} />
              <Route path="drivex/shared/:shareLink" element={<DrivexPage />} />
              <Route path="chat" element={<ChatRedirect />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="profile" element={<ProfilePage />} />
            </>
        </Route>
        )}

        {/* Settings routes - rendered as modal overlay on top of mail (disabled in helpdesk mode) */}
        {!isHelpdeskMode() && (
          <Route
            path="/settings/*"
            element={
              <ProtectedRoute>
                <>
                  <HybridMailLayout />
                  <Routes>
                    <Route element={<SettingsLayout />}>
                      <Route index element={<Navigate to="/settings/system" replace />} />
                      <Route path="system" element={<SystemSettings />} />
                      <Route path="keyboard-shortcuts" element={<KeyboardShortcuts />} />
                      <Route path="integrations" element={<IntegrationsSettings />} />
                      <Route path="integrations/extensions" element={<IntegrationsSettings />} />
                      <Route path="integrations/developer" element={<IntegrationsSettings />} />
                      <Route path="integrations/channels" element={<IntegrationsSettings />} />
                      <Route path="preset" element={<AccountSettings />} />
                      <Route path="notification" element={<NotificationSettings />} />
                      <Route path="notification/desktop" element={<NotificationSettings />} />
                      <Route path="notification/priority" element={<NotificationSettings />} />
                      <Route path="notification/quiet" element={<NotificationSettings />} />
                      <Route path="notification/mail" element={<NotificationSettings />} />
                      <Route path="mail-view" element={<MailViewOptions />} />
                      <Route path="compose" element={<ComposeSettings />} />
                      <Route path="compose-customization" element={<ComposeSettings />} />
                      <Route path="offline" element={<AccountSettings />} />
                      <Route path="conversation" element={<AccountSettings />} />
                      <Route path="folders" element={<FoldersSettings />} />
                      <Route path="labels" element={<LabelsSettings />} />
                      <Route path="filters" element={<FiltersSettings />} />
                      <Route path="archive-policy" element={<AccountSettings />} />
                      <Route path="hotkeys" element={<KeyboardShortcuts />} />
                      <Route path="out-of-office" element={<OutOfOfficeSettings />} />
                      <Route path="sharing-delegation" element={<AccountSettings />} />
                      <Route path="mail-accounts" element={<MailAccountsSettings />} />
                      <Route path="signature" element={<SignatureSettings />} />
                      <Route path="app-passwords" element={<AppPasswordsSettings />} />
                      <Route path="anti-spam" element={<AntiSpamSettings />} />
                      <Route path="import-export" element={<ImportExportSettings />} />
                      <Route path="groups" element={<AccountSettings />} />
                      <Route path="shared-mailbox" element={<AccountSettings />} />
                      <Route path="account" element={<AccountSettings />} />
                    </Route>
                  </Routes>
                </>
              </ProtectedRoute>
            }
          />
        )}

        {/* 404 */}
        <Route path="*" element={<Navigate to={isHelpdeskMode() ? '/support/dashboard' : '/'} replace />} />

        {/* Helpdesk access from normal webmail mode — full staff interface */}
        {!isHelpdeskMode() && (
          <Route
            path="/helpdesk"
            element={
              <ProtectedRoute>
                <HelpdeskLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/helpdesk/dashboard" replace />} />
            <Route path="dashboard" element={<HelpdeskDashboard />} />
            <Route path="tickets" element={<HelpdeskTickets />} />
            <Route path="tickets/:id" element={<HelpdeskTicketDetail />} />
            <Route path="queue" element={<HelpdeskAgentQueue />} />
            <Route path="customers" element={<HelpdeskCustomers />} />
            <Route path="knowledge" element={<HelpdeskKnowledgeBase />} />
            <Route path="reports" element={<HelpdeskReports />} />
            <Route path="settings" element={<HelpdeskSettings />} />
            <Route path="*" element={<Navigate to="/helpdesk/dashboard" replace />} />
          </Route>
        )}
      </Routes>
    </>
  );
};

const App: React.FC = () => {
  const { actualMode, accentColor, selectedPalette, setMode, setAccentColor } = useThemeStore();
  const { isAuthenticated } = useAuthStore();
  const { settings, fetchSettings } = useSettingsStore();
  
  // DEBUG: Log localStorage state on mount
  useEffect(() => {
    console.log('=== APP MOUNT - CHECKING LOCALSTORAGE ===');
    const stored = localStorage.getItem('zmail-compose-tabs');
    console.log('[App] localStorage raw value:', stored);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        console.log('[App] localStorage parsed:', parsed);
      } catch (e) {
        console.error('[App] Failed to parse localStorage:', e);
      }
    } else {
      console.warn('[App] No zmail-compose-tabs in localStorage!');
    }
    
    // Also log current store state
    const storeState = useComposeTabsStore.getState();
    console.log('[App] Current store state:', {
      chatOpen: storeState.chatOpen,
      showingCompose: storeState.showingCompose,
      tabs: storeState.tabs.length
    });
  }, []);
  
  // Proactive token refresh — refresh 5 minutes before expiry
  useEffect(() => {
    if (!isAuthenticated) return;
    const scheduleRefresh = () => {
      const token = localStorage.getItem('accessToken');
      if (!token) return;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiresAt = payload.exp * 1000;
        const now = Date.now();
        // Refresh 5 minutes before expiry, minimum 30 seconds from now
        const refreshIn = Math.max(expiresAt - now - 5 * 60 * 1000, 30000);
        return setTimeout(async () => {
          const refreshToken = localStorage.getItem('refreshToken');
          if (!refreshToken) return;
          try {
            const { default: axios } = await import('axios');
            const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
            const res = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });
            const data = res.data?.data || res.data;
            if (data.accessToken) {
              localStorage.setItem('accessToken', data.accessToken);
              if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
              useAuthStore.setState({ token: data.accessToken, isAuthenticated: true });
            }
          } catch { /* interceptor will handle on next API call */ }
          // Re-schedule after refresh
          const tid = scheduleRefresh();
          if (tid) timerRef = tid;
        }, refreshIn);
      } catch { return undefined; }
    };
    let timerRef = scheduleRefresh();
    return () => { if (timerRef) clearTimeout(timerRef); };
  }, [isAuthenticated]);

  // Fetch settings and sync theme on app load when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      console.log('🌙 App: User authenticated, fetching settings');
      fetchSettings();
      // Request desktop notification permission for background email alerts
      requestNotificationPermission();
    }
  }, [isAuthenticated, fetchSettings]);
  
  // Sync theme settings when settings are loaded
  useEffect(() => {
    const validModes: string[] = ['light', 'dark', 'system'];
    if (settings.theme) {
      console.log('🌙 App: Syncing theme from settings:', settings.theme);
      if (validModes.includes(settings.theme)) {
        setMode(settings.theme as 'light' | 'dark' | 'system');
      } else {
        // It's a palette ID (e.g. 'dark-charcoal') — use setSelectedPalette which derives actualMode
        useThemeStore.getState().setSelectedPalette(settings.theme);
      }
    }
    if (settings.accentColor) {
      console.log('🌙 App: Syncing accent color from settings:', settings.accentColor);
      setAccentColor(settings.accentColor as 'blue' | 'green' | 'yellow' | 'red' | 'pink' | 'gray' | 'teal' | 'purple' | 'orange');
    }
  }, [settings.theme, settings.accentColor, setMode, setAccentColor]);
  
  // Memoize theme creation to avoid unnecessary re-renders
  const theme = useMemo(() => {
    console.log('🌙 App creating theme - actualMode:', actualMode, 'accentColor:', accentColor, 'selectedPalette:', selectedPalette);
    const palette = themePalettes.find(p => p.id === selectedPalette);
    const paletteColors = palette ? { sidebar: palette.sidebar, content: palette.content } : undefined;
    return createAppTheme(actualMode, accentColor, paletteColors);
  }, [actualMode, accentColor, selectedPalette]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrandingProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </BrandingProvider>
    </ThemeProvider>
  );
};

export default App;
